/* The footer's behaviour: <newsletter-form> and <footer-menus> (snippets/footer-brand.liquid). */

/**
 * <newsletter-form> — the footer sign-up (snippets/newsletter-form.liquid). Posts
 * { email } as JSON to data-webhook. Validates the email and the consent box first,
 * shows the result in the form's live region, and keeps the button's content (a
 * spinner shows while sending).
 */
if (!customElements.get('newsletter-form')) {
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  class NewsletterForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.abortController = new AbortController();
      this.form.addEventListener('submit', (event) => this.submit(event), { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async submit(event) {
      event.preventDefault();
      const input = this.form.querySelector('[name="email"]');
      const consent = this.form.querySelector('[name="consent"]');
      const button = this.form.querySelector('[type="submit"]');
      const email = input.value.trim();

      if (!EMAIL.test(email)) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        this.respond(this.dataset.invalidText, 'error');
        return;
      }
      input.removeAttribute('aria-invalid');
      if (consent && !consent.checked) {
        consent.reportValidity();
        return;
      }
      if (!this.dataset.webhook) return;

      button.disabled = true;
      button.classList.add('is-loading');
      this.respond('');
      try {
        const response = await fetch(this.dataset.webhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.form.reset();
        this.respond(this.dataset.successText, 'success');
      } catch (error) {
        console.error('[newsletter-form]', error);
        this.respond(this.dataset.errorText, 'error');
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    respond(text, type = '') {
      const box = this.form.querySelector('.newsletter-form__response');
      if (!box) return;
      box.textContent = text || '';
      box.hidden = !text;
      box.dataset.type = type;
    }
  }

  customElements.define('newsletter-form', NewsletterForm);
}

/**
 * <footer-menus> — the footer's link menus as <details>. From 768px they stay open side by
 * side (the summary is a plain heading); on phones each opens on tap.
 */
if (!customElements.get('footer-menus')) {
  const WIDE = '(min-width: 768px)';

  class FooterMenus extends HTMLElement {
    connectedCallback() {
      this.query = window.matchMedia(WIDE);
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.query.addEventListener('change', () => this.sync(), { signal });
      // Wide: the headings don't fold the menus.
      this.addEventListener(
        'click',
        (event) => {
          if (this.query.matches && event.target.closest('summary')) event.preventDefault();
        },
        { signal }
      );
      this.sync();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    sync() {
      const wide = this.query.matches;
      this.querySelectorAll('details').forEach((details) => {
        details.open = wide;
        details.querySelector('summary')?.setAttribute('tabindex', wide ? '-1' : '0');
      });
    }
  }

  customElements.define('footer-menus', FooterMenus);
}
